<script lang="ts" setup>
import type { Data } from '@solfacil/girassol/dist/types/components/list/types'
import type { Type } from '@solfacil/girassol/dist/types/components/tag/types'
import type { RightClickContextMenuProps } from '../RightClickMenu/RightClickContextMenu.types'
import type { FinancingsList, UseFinancingsParams } from '~/store-v2/financings/useFinancings'
import type { GeneralStatuses } from '~/utils/financing-flow/Financing'
import { SolList, useToast } from '@solfacil/girassol'
import IconBack from '~icons/material-symbols/arrow-back-ios-new'
import Loading from '~/components/loading/index.vue'
import { useFinancingRoute } from '~/composables/financing/useFinancingRoute'
import { STATUSES_MAPPING } from '~/services-v2/financial-bff/financial-bff.consts'
import currencyUtils from '~/utils/currency'
import { COMBO_TYPE_FILTER, comboTypeFromParams } from '~/utils/financing-flow/comboType'
import { conventionalNameFormat } from '~/utils/formatter/name'
import FinancingListPagination from '../financingList/Pagination.vue'
import RightClickContextMenu from '../RightClickMenu/RightClickContextMenu.vue'
import InstallationTime from './InstallationTime.vue'
import PaymentModelTime from './PaymentModelTime.vue'
import ProposalTime from './ProposalTime.vue'

const props = defineProps<{
  loading: boolean
  list: FinancingsList
  params: UseFinancingsParams
  errorFinancings: Error | null
  tabType: 'all' | 'reassessment'
  prefetchPage?: (pageNumber: number) => void
}>()
const emit = defineEmits<{
  (e: 'setFilters', v: UseFinancingsParams)
  (e: 'clearFilters'): void
}>()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const isLargeScreen = useMediaQuery('(min-width: 1024px)')
// From this width the Combo tag gets its own column next to the status without truncating the customer name;
// below it the tag is rendered inside the status cell, after the status tag (side by side or wrapped below)
const COMBO_COLUMN_MIN_VIEWPORT_WIDTH = 1366
const isComboColumnVisible = useMediaQuery(`(min-width: ${COMBO_COLUMN_MIN_VIEWPORT_WIDTH}px)`)
const page = ref(props.params.page)
const financingList = ref()

const hasNextPagination = ref((props.list as { has_next?: boolean })?.has_next || false)
const contextMenu = ref<null | RightClickContextMenuProps>(null)

const hasAdditionalFilters = computed(() => {
  if (props.tabType !== 'reassessment')
    return false

  const { steps, status, query } = props.params

  return (
    (steps && steps.length > 0)
    || (status && status.length > 0)
    || (query && query.trim() !== '')
    || comboTypeFromParams(props.params) !== COMBO_TYPE_FILTER.ALL
  )
})

const emptyStateTitle = computed(() => {
  if (props.tabType === 'reassessment') {
    return hasAdditionalFilters.value
      ? t('financing.notFound.title')
      : t('reassessment.notFound.title')
  }
  return t('financing.notFound.title')
})

const emptyStateSubtitle = computed(() => {
  if (props.tabType === 'reassessment') {
    return hasAdditionalFilters.value
      ? t('reassessment.notFound.emptyFilter')
      : t('reassessment.notFound.subtitle')
  }
  return t('financing.notFound.subtitle')
})

const { buildRouteUrl } = useFinancingRoute()

function fetchFinancingList(data: FinancingsList) {
  try {
    financingList.value = data
  }
  catch (error) {
    console.error(error)
    toast.createErrorToast({
      title: t('financing.list.error.fetching.title'),
      description: t('financing.list.error.fetching.description'),
    })
  }
}

const financingListProps = computed(() => ({
  headers: {
    id: {
      text: t('financing.list.headers.id'),
      width: '72px',
    },
    customer_name: {
      text: t('financing.list.headers.customer_name'),
    },
    project_value: {
      text: t('financing.list.headers.project_value'),
      width: '118px',
    },
    step: {
      text: t('financing.list.headers.step'),
      width: '150px',
    },
    days_info: {
      width: '160px',
    },
    is_combo: {
      width: '112px',
      hide: true,
    },
    status: {
      width: '140px',
      hide: true,
    },
  },
  sortPositions: {
    mobile: [
      ['customer_name', 'status'],
      ['id', 'project_value'],
      ['step', 'days_info'],
    ],
    desktop: [
      'customer_name',
      'id',
      'project_value',
      'step',
      'days_info',
      ...(isComboColumnVisible.value ? ['is_combo'] : []),
      'status',
    ],
  },
}))

function renderStatus(status: GeneralStatuses): Type {
  return STATUSES_MAPPING[status].color || 'informative'
}

function buildRouteUrlForList(item: Data): string {
  return buildRouteUrl(item)
}

function goToStepScreen(item: Data, event: MouseEvent, contextMenu = false) {
  const url = buildRouteUrlForList(item)
  handleNavigation(event, item, url, contextMenu)
}

function handleNavigation(
  event: MouseEvent | KeyboardEvent | null,
  item: Data,
  url: string,
  contextMenu: boolean,
) {
  const isOldJourney = item.new_journey === false && item.new_journey !== null

  const shouldOpenInNewTab = event?.ctrlKey || contextMenu || isOldJourney

  shouldOpenInNewTab
    ? window.open(url, '_blank')
    : router.push(url)
}

watch(() => props.params.page, (newPage) => {
  if (newPage !== page.value) {
    page.value = newPage
  }
})

function setPage(value: number) {
  emit('setFilters', { ...props.params, page: value } as UseFinancingsParams)
}

function onContextMenu(item: Data, event: MouseEvent) {
  event.preventDefault()

  contextMenu.value = {
    x: event.pageX,
    y: event.pageY,
    actions: [
      {
        label: 'Abrir em uma nova aba',
        action: () => {
          goToStepScreen(item, event, true)
        },
      },
    ],
  }

  window.addEventListener('click', () => contextMenu.value = null, { once: true })
}

function clearFiltersApplied() {
  emit('clearFilters')
}

watch(() => props.list, (_list) => {
  if (_list) {
    fetchFinancingList(_list)
    hasNextPagination.value = (_list as { has_next?: boolean })?.has_next || false
  }
})

onMounted(() => {
  if (props.list)
    fetchFinancingList(props.list)
})

function getListData() {
  return financingList.value?.data.map(item => ({
    ...item,
    days_info: '-',
  })) as Data[]
}
</script>

<template>
  <template v-if="contextMenu">
    <RightClickContextMenu :x="contextMenu.x" :y="contextMenu.y" :actions="contextMenu.actions" />
  </template>
  <div class="container-list-financing">
    <div v-if="loading" class="loading">
      <Loading />
    </div>
    <div v-else>
      <SolAlert
        v-if="financingList?.data?.length && tabType === 'reassessment'"
        id="reassessment-alert"
        :title="t('reassessment.alert.title')"
        feedback="informative"
        class="mb-4xs"
      >
        {{ t('reassessment.alert.description') }}
      </SolAlert>
      <div class="financing-list">
        <SolList
          v-if="financingList?.data.length"
          id="financing-list"
          v-bind="financingListProps"
          :hide-menu="true"
          :data="getListData()"
          @listitem:click="goToStepScreen"
          @listitem:contextmenu="onContextMenu"
        >
          <template #column:customer_name="{ data, index }">
            <div id="financing-list-field-name" class="items-center">
              <p class="fonts-body-small-regular text-neutral-low-light">
                {{ t('financing.list.headers.customer_name') }}
              </p>
              <div class="flex w-full items-center mt-quark">
                <p class="fonts-body-medium-bold">
                  {{ conventionalNameFormat(String(data.value)) }}
                </p>

                <SolTag
                  v-if="financingList.data[index].flow_provider === 'reassessment'"
                  :id="`tag-financing-reassessment-${data.value}`"
                  class="ml-nano"
                  type="secondary"
                  text="Repescado"
                  variant="fill"
                  size="small"
                />
              </div>
            </div>
          </template>

          <template #column:project_value="{ data, index }">
            <div class="items-center">
              <p class="fonts-body-small-regular text-neutral-low-light">
                {{ t('financing.list.headers.project_value') }}
              </p>
              <p class="fonts-body-medium-regular">
                {{ typeof data.value === 'number' ? currencyUtils.formatBRL(data.value) : '-' }}
                <SolTag
                  v-if="financingList.data[index].percentage_paid"
                  :id="`financing-paid-${financingList.data[index].id}`"
                  :type="financingList.data[index].percentage_paid === 100 ? 'positive' : 'informative'"
                  size="small"
                >
                  {{ t('financing.list.tags.paid_percentage', { percentage: financingList.data[index].percentage_paid }) }}
                </SolTag>
              </p>
            </div>
          </template>

          <template #column:days_info="{ index }">
            <div class="items-center">
              <template v-if="financingList.data[index].step === 'installation'">
                <InstallationTime
                  :item="financingList.data[index]"
                />
              </template>
              <template v-else-if="financingList.data[index].step === 'payment_model'">
                <PaymentModelTime
                  :item="financingList.data[index]"
                />
              </template>
              <template v-else>
                <ProposalTime
                  :item="financingList.data[index]"
                />
              </template>
            </div>
          </template>

          <template #column:is_combo="{ data, index }">
            <div :data-testid="`listitem-financing-list-${index}-is_combo`" class="flex items-center">
              <FinancingsComboTag
                :id="`financing-combo-${financingList.data[index].id}`"
                :connect-id="`financing-combo-connect-${financingList.data[index].id}`"
                :is-combo="Boolean(data.value)"
                :is-combo-connect="financingList.data[index].is_combo_connect"
                :combo-text="t('financing.list.tags.combo')"
                :combo-connect-text="t('financing.list.tags.combo_connect')"
              />
            </div>
          </template>

          <template v-if="!isComboColumnVisible" #column:status="{ data, index }">
            <div :data-testid="`listitem-financing-list-${index}-status`" class="flex w-full flex-wrap items-center gap-nano">
              <SolTag
                :id="`financing-status-${data.value}`"
                class="lg:site:w-full"
                :type="renderStatus(data.value as GeneralStatuses)"
              >
                {{ t(STATUSES_MAPPING[data.value as GeneralStatuses].textKey) }}
              </SolTag>
              <FinancingsComboTag
                :id="`financing-combo-${financingList.data[index].id}`"
                :connect-id="`financing-combo-connect-${financingList.data[index].id}`"
                :is-combo="financingList.data[index].is_combo"
                :is-combo-connect="financingList.data[index].is_combo_connect"
                :combo-text="t('financing.list.tags.combo')"
                :combo-connect-text="t('financing.list.tags.combo_connect')"
              />
            </div>
          </template>

          <template #value:status="{ data }">
            <SolTag
              :id="`financing-status-${data.value}`"
              class="lg:site:w-full"
              :type="renderStatus(data.value as GeneralStatuses)"
            >
              {{ t(STATUSES_MAPPING[data.value as GeneralStatuses].textKey) }}
            </SolTag>
          </template>

          <template #value:step="{ data }">
            {{ t(`financing.list.step.${data.value}`) }}
          </template>
        </SolList>
        <section v-else class="empty-state">
          <template v-if="errorFinancings">
            <SolEmptyState
              id="empty-list"
              :title="$t('financing.ErrorEmptyState.title')"
              :subtitle="$t('financing.ErrorEmptyState.subtitle')"
              variant="empty-list"
            />
          </template>
          <template v-else>
            <SolEmptyState
              :id="tabType === 'reassessment' ? 'reassessment-empty' : 'project-not-found'"
              :title="emptyStateTitle"
              :subtitle="emptyStateSubtitle"
              variant="search"
            >
              <template v-if="tabType !== 'reassessment'" #action>
                <div class="flex gap-2 inline-flex show-md">
                  <SolButton
                    id="btn-back-list"
                    class="flex items-center justify-center text-center"
                    :size="isLargeScreen ? 'medium' : 'small' "
                    @click="clearFiltersApplied()"
                  >
                    <template #icon-left>
                      <IconBack class="icon-size-small" />
                    </template>
                    {{ t('app.backList') }}
                  </SolButton>
                </div>
              </template>
            </SolEmptyState>
          </template>
        </section>
      </div>
      <FinancingListPagination
        v-if="financingList?.data.length"
        :page="page"
        :has-next-pagination="hasNextPagination"
        :prefetch-page="props.prefetchPage || (() => {})"
        @change-page="setPage"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container-list-financing {
  overflow-x: auto;
  text-overflow: ellipsis;
  white-space: nowrap;

  .loading {
    @apply flex justify-center items-center h-96;
  }
  .empty-state {
    @apply w-full pt-2xs;

    .sol-empty-state-core {
      @apply p-0 max-w-full;
    }
  }
}
</style>
